import { Field } from "@rosc/ui/v32/field";
import { Textarea } from "@rosc/ui/v32/input";
import { SegmentedControl } from "@rosc/ui/v32/segmented-control";
import type { JSX } from "react";
import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  type PreChartLabMode,
  type PreChartLabs,
  parseTestList,
} from "@/lib/pre-chart-labs";

// The three "Pre-chart labs" choices: All values, Abnormal only, Only these
// tests (with the list). Used by the setting and by one pre-chart's override.
export const PreChartLabsForm = ({
  value,
  onChange,
  testId = "pre-chart-labs",
}: {
  value: PreChartLabs;
  onChange: (value: PreChartLabs) => void;
  testId?: string;
}): JSX.Element => {
  const { t } = useTranslation();
  const listId = useId();
  // The list as typed; saved as names (parseTestList) on every change.
  const [text, setText] = useState(() => (value.tests ?? []).join(", "));
  // Shown here even while the list is empty (then saved as All values).
  const [mode, setMode] = useState<PreChartLabMode>(value.mode);
  const hint: Record<PreChartLabMode, string> = {
    all: t("preChart.labs.allHint"),
    abnormal: t("preChart.labs.abnormalHint"),
    tests: t("preChart.labs.testsHint"),
  };
  return (
    <div className="space-y-3 font-v3" data-testid={testId}>
      <SegmentedControl
        label={t("preChart.labs.title")}
        segments={[
          {
            value: "all",
            label: t("preChart.labs.segment.all"),
            testId: `${testId}-all`,
          },
          {
            value: "abnormal",
            label: t("preChart.labs.segment.abnormal"),
            testId: `${testId}-abnormal`,
          },
          {
            value: "tests",
            label: t("preChart.labs.segment.tests"),
            testId: `${testId}-tests`,
          },
        ]}
        stretch
        value={mode}
        onValueChange={(next) => {
          setMode(next);
          onChange(
            next === "tests"
              ? { mode: next, tests: parseTestList(text) }
              : { mode: next },
          );
        }}
      />
      <p className="text-[13px] leading-[18px] text-v3-text-3">{hint[mode]}</p>
      {mode === "tests" && (
        <Field
          hint={t("preChart.labs.listHint")}
          htmlFor={listId}
          label={t("preChart.labs.listLabel")}
        >
          <Textarea
            className="min-h-20"
            data-testid={`${testId}-list`}
            id={listId}
            placeholder="A1c, LDL, eGFR, ACR"
            value={text}
            onChange={(event) => {
              setText(event.target.value);
              onChange({
                mode: "tests",
                tests: parseTestList(event.target.value),
              });
            }}
          />
        </Field>
      )}
    </div>
  );
};
